import React from "react";
import { ReactRenderer } from "./lib/ReactRenderer";
import DateSelectorComponent from "./componentlib/dateselector/DateSelectorComponent";
import SwipeNavigation from "./lib/swipeNavigation";
import { DateTime } from "luxon";
const Arrow = ({ left }) => (React.createElement("svg", { viewBox: "0 0 24 24", width: "20", height: "20", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", "aria-hidden": "true" },
    React.createElement("path", { d: left ? "m15 6-6 6 6 6" : "m9 6 6 6-6 6" })));
/** Top 5: the films that sold the most tickets in a month, with the month to step through. */
export default class TicketSalesComponent extends React.Component {
    constructor(props) {
        super(props);
        /** The current month is the last one that has sales: a month in the future has nothing to show. */
        this.lastMonth = DateTime.local().startOf("month");
        this.listRef = React.createRef();
        this.swipeRef = React.createRef();
        this.navigating = false;
        let startDt = DateTime.local().startOf("month").plus({ months: -1 });
        let endDt = startDt.endOf("month");
        this.state = {
            isLoaded: false,
            startDt: startDt,
            endDt: endDt,
        };
    }
    componentDidMount() {
        this.getTicketSales();
    }
    fetchTicketSales(startDt) {
        return fetch(`/bpapi/ticketsales/period/?startDt=${startDt.toFormat("yyyy-MM-dd")}&endDt=${startDt.endOf("month").toFormat("yyyy-MM-dd")}`, { method: 'GET' })
            .then((response) => response.ok ? response.json() : Promise.reject(`Kunne ikke indlæse billetsalg (${response.status} ${response.statusText})`));
    }
    getTicketSales() {
        const startDt = this.state.startDt;
        this.fetchTicketSales(startDt)
            .then((result) => {
            // The answer for a month that has been left since: the month on screen has its own on the way.
            if (!startDt.equals(this.state.startDt))
                return;
            this.setState({ ticketSales: result, error: null, isLoaded: true });
        })
            .catch(error => {
            if (!startDt.equals(this.state.startDt))
                return;
            this.setState({ ticketSales: null, error: error, isLoaded: true });
        });
    }
    /**
     * Shows a month picked in the month picker. The rows of the month that is left stay on screen,
     * dimmed, until the new ones arrive, and the list keeps the tallest height it has had: a card that
     * shrank for the time of the fetch made the page jump, and a page scrolled to its end lost the
     * bottom of the card.
     */
    showMonth(startDt) {
        const list = this.listRef.current;
        this.setState({
            startDt: startDt,
            endDt: startDt.endOf("month"),
            isLoaded: false,
            error: null,
            listMinHeight: list ? list.offsetHeight : undefined
        }, () => this.getTicketSales());
    }
    // Forrige måned (-1) and Næste måned (1), from the buttons or a swipe. The rows slide out while the
    // next month is fetched, and its rows slide in from the other side. One step at a time: a second
    // swipe before the first has landed is dropped, and so is a swipe past the current month.
    navigate(increment) {
        var _a;
        if (this.navigating || !this.state.isLoaded)
            return;
        if (increment > 0 && this.state.startDt >= this.lastMonth)
            return;
        this.navigating = true;
        const startDt = this.state.startDt.plus({ months: increment }).startOf("month");
        const listMinHeight = (_a = this.listRef.current) === null || _a === void 0 ? void 0 : _a.offsetHeight;
        const swipe = this.swipeRef.current;
        const slidOut = swipe.slideOut(increment);
        // A month that cannot be fetched is still the month shown, with the message in the place of the rows.
        const shown = (ticketSales, error) => new Promise((resolve) => {
            this.setState({
                startDt: startDt,
                endDt: startDt.endOf("month"),
                ticketSales: ticketSales,
                error: error,
                isLoaded: true,
                listMinHeight: listMinHeight
            }, () => resolve(swipe.slideIn(increment)));
        });
        Promise.all([this.fetchTicketSales(startDt), slidOut])
            .then(([ticketSales]) => shown(ticketSales, null))
            .catch((error) => slidOut.then(() => shown(null, error)))
            .finally(() => { this.navigating = false; });
    }
    list() {
        const sales = this.state.ticketSales;
        const loading = !this.state.isLoaded;
        // The first fetch, or one after a failure, has nothing to keep on screen.
        if (loading && !(sales && sales.length > 0)) {
            return (React.createElement("div", { className: "bi-top-empty" },
                React.createElement("i", { className: "fa-regular fa-spinner-third fa-spin" })));
        }
        if (this.state.error) {
            return (React.createElement("div", { className: "bi-top-empty" }, this.state.error));
        }
        if (!sales || sales.length === 0) {
            return (React.createElement("div", { className: "bi-top-empty" }, "Ingen data"));
        }
        return sales.slice(0, 5).map((x, i) => (React.createElement("div", { className: "bi-top-row", key: i },
            React.createElement("span", { className: "bi-top-rank" }, i + 1),
            React.createElement("span", { className: "bi-top-title" },
                x.contentTitle,
                React.createElement("span", { className: "bi-top-showings" }, x.showings === 1 ? "1 visning" : `${x.showings} visninger`)),
            React.createElement("span", { className: "bi-top-sales" }, x.salesInPeriod))));
    }
    render() {
        return (React.createElement("section", { className: "bi-sl-section" },
            React.createElement("div", { className: "bi-sl-section-head" },
                React.createElement("h2", null, "Top 5")),
            React.createElement("div", { className: "bi-sl-card bi-top-card" },
                React.createElement("div", { className: "bi-top-month" },
                    React.createElement("button", { type: "button", className: "bi-top-step", "aria-label": "Forrige m\u00E5ned", onClick: () => this.navigate(-1) },
                        React.createElement(Arrow, { left: true })),
                    React.createElement(DateSelectorComponent, { dt: this.state.startDt.toFormat("yyyy-MM-dd"), format: "MMMM yyyy", onDateSelected: (dt) => this.showMonth(DateTime.fromISO(dt).startOf("month")), maxDt: this.lastMonth, mode: "month" }),
                    React.createElement("button", { type: "button", className: "bi-top-step", "aria-label": "N\u00E6ste m\u00E5ned", onClick: () => this.navigate(1), disabled: this.state.startDt >= this.lastMonth },
                        React.createElement(Arrow, null))),
                React.createElement(SwipeNavigation, { ref: this.swipeRef, onPrevious: () => this.navigate(-1), onNext: () => this.navigate(1) },
                    React.createElement("div", { ref: this.listRef, className: `bi-top-list${this.state.isLoaded ? "" : " bi-top-list-loading"}`, style: { minHeight: this.state.listMinHeight }, "aria-busy": !this.state.isLoaded }, this.list()))),
            React.createElement("p", { className: "bi-sl-section-note" }, "Solgte billetter pr. film i perioden.")));
    }
}
ReactRenderer.render("ticketsalescomponent", (elem) => React.createElement(TicketSalesComponent, Object.assign({}, elem.dataset)));
